<script setup lang="ts">
// 统一Store导入
import { useTTSStore, useConfigStore } from "@/store"
import { useVoiceStore } from "@/store/voice"
import { useGuideStore } from '@/store/guide'
import { useEntitlementStore } from '@/store/entitlement'
import { storeToRefs } from 'pinia'
import Main from "@/layouts/MainLayout.vue";
import WebStore from "./store/web-store";
import { onMounted, onBeforeUnmount } from "vue";
import { useI18n } from 'vue-i18n';
import i18n from './assets/i18n/i18n';

// 导入 Composables - 使用新架构
import { useTheme } from '@/composables/useTheme'




// 使用新的模块化Store
const ttsStore = useTTSStore()
const configStore = useConfigStore()
const voiceStore = useVoiceStore()
const guideStore = useGuideStore()
const entitlementStore = useEntitlementStore()
const store = new WebStore()
const { locale } = useI18n()



// 使用主题管理
const { isDarkTheme } = useTheme()

// 使用 storeToRefs 保持响应性
const {
  showUserGuide,
  currentGuideStep,
  guideSteps,
  currentPageTitle,
  currentPageDescription,
  currentStep,
  highlightStyle,
  guideCardStyle
} = storeToRefs(guideStore)

// 方法可以直接解构
const {
  updateGuidePosition,
  skipGuide,
  handleNextStep,
  handlePrevStep,
  setGuideStep,
  startGuide
} = guideStore

// 监听showUserGuide状态变化（已移除调试日志）

// 确保默认值被正确设置
if (!store.get("api")) {
  store.set("api", 5);  // 设置默认 API 为免费TTS服务
  configStore.config.api = 5;
}

// 确保 FormConfig 存在
if (!store.get("FormConfig")) {
  const defaultConfig = {
    "默认": {
      languageSelect: "zh-CN",
      voiceSelect: "zh-CN-XiaoxiaoNeural",
      voiceStyleSelect: "Default",
      role: "",
      speed: 1,
      pitch: 1,
      api: 5 // 设置为免费TTS服务
    }
  };
  // ✅ 使用 store 的 setFormConfig 方法，类型安全
  configStore.setFormConfig(defaultConfig);
}

// 添加一个通用的事件阻止方法
const stopEvent = (event?: Event): void => {
  if (event) {
    event.stopPropagation()
    event.preventDefault()
  }
}

// 确保在访问前配置已正确初始化（如果 store 中没有数据）
if (!configStore.FormConfig || Object.keys(configStore.FormConfig).length === 0) {
  const fallbackConfig = store.get("FormConfig") || {
    "默认": {
      languageSelect: "zh-CN",
      voiceSelect: "zh-CN-XiaoxiaoNeural",
      voiceStyleSelect: "Default",
      role: "",
      speed: 1,
      pitch: 1,
      api: 5
    }
  };
  // ✅ 使用类型安全的方法
  configStore.setFormConfig(fallbackConfig);
}

onMounted(async () => {
  // 初始化TTS Core Store
  try {
    await ttsStore.initialize();
  } catch (error) {
    console.warn('TTS Store初始化失败:', error);
  }

  // 初始化权益信息（配额、套餐等）
  try {
    await entitlementStore.initialize();
  } catch (error) {
    console.warn('权益信息初始化失败:', error);
  }

  // 异步初始化语音数据 - 不阻塞页面渲染
  voiceStore.initializeVoiceData()
    .catch((error: any) => {
      console.error('语音数据初始化失败:', error);
    });

  // 确保语言设置正确
  if (locale.value !== i18n.global.locale.value) {
    i18n.global.locale.value = locale.value as any;
  }

  // 添加窗口大小变化监听
  window.addEventListener('resize', updateGuidePosition);

  // 监听全局引导启动事件
  window.addEventListener('start-user-guide', () => {
    startGuide()
  });
  
  // 暴露引导函数到全局，便于调试和从其他组件调用
  // 调试工具函数（开发环境使用）
  if (import.meta.env.DEV) {
    window.__startGuide = startGuide;
    window.__clearGuideStatus = () => {
      localStorage.removeItem('user_guide_completed')
      localStorage.removeItem('user_guide_skipped')
      localStorage.removeItem('user_guide_completed_time')
      localStorage.removeItem('user_guide_skipped_time')
      console.log('引导状态已清理，刷新页面后将重新显示引导')
    };
  }
});

// 组件卸载时清理
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateGuidePosition);
  
  // 移除全局主题切换事件监听
  window.removeEventListener('toggle-theme-event', () => {});
});
</script>

<template>
  <div
    :class="[
      'app-container',
      { 'dark-theme': isDarkTheme }
    ]"
  >
    <!-- 主容器 -->
    <div class="main-container">
      <!-- 内容区域 -->
      <div class="content-wrapper">
        <Main ref="mainRef" />
      </div>
    </div>

    <!-- 用户引导系统 -->
    <div v-if="showUserGuide" class="guide-overlay" @click="stopEvent">
      <!-- 高亮区域 -->
      <div class="guide-highlight" :style="highlightStyle"></div>

      <!-- 引导卡片 -->
      <div class="guide-card" :style="guideCardStyle" @click.stop>
        <div class="guide-card-header">
          <h2>{{ currentPageTitle }} - 使用引导</h2>
          <p>{{ currentPageDescription }}</p>
          <button class="guide-skip-btn" @click="skipGuide" title="跳过引导">
            ✕
          </button>
        </div>
        
        <div class="guide-card-content">
          <div class="guide-step">
            <div class="guide-step-number">{{ (currentGuideStep || 0) + 1 }}/{{ guideSteps?.length || 0 }}</div>
            <h3>
              <i class="guide-icon el-icon-info-filled"></i>
              {{ currentStep.title }}
            </h3>
            <p>{{ currentStep.content }}</p>
            <div v-if="currentStep.hint" class="guide-hint">
              <i class="el-icon-info"></i> {{ currentStep.hint }}
            </div>
          </div>
        </div>
        
        <div class="guide-card-footer">
          <div class="guide-indicators">
            <div
              v-for="(_, index) in guideSteps"
              :key="`guide-step-${index}`"
              :class="['guide-indicator', { active: index === currentGuideStep }]"
              @click="setGuideStep(index)"
            ></div>
          </div>
          
          <div class="guide-controls">
            <button
              class="guide-btn secondary"
              @click="skipGuide"
            >
              跳过
            </button>
            <div class="guide-nav-buttons">
              <button
                class="guide-btn"
                :disabled="currentGuideStep === 0"
                @click="handlePrevStep"
              >
                上一步
              </button>
              <button
                class="guide-btn primary"
                @click="handleNextStep"
              >
                {{ currentGuideStep === (guideSteps?.length || 0) - 1 ? '完成' : '下一步' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>




  </div>
</template>

<style>
@import '@/assets/styles/guide.css';
@import '@/assets/styles/task-notification.css';

/* 全局变量 */
/* 全局变量由 tokens.css 提供 */

/* 基本样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: var(--font-family);
  background-color: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition-normal);
}

/* 应用容器 */
.app-container {
  min-height: 100vh;
  background-color: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition-normal);
  position: relative;
  /* padding-top: var(--tts-nav-height);  Removed to avoid double padding with page-level styles */
}

/* 主容器 */
.main-container {
  display: flex;
  min-height: 100vh;
}

/* 内容区域 */
.content-wrapper {
  flex: 1;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
}

/* 主内容区域卡片样式 */
.input-area-card,
.batch-area-card,
.config-page-container,
.doc-page-container {
  margin: 0;
  border-radius: 0;
  border: none;
  box-shadow: none;
  background: transparent;
  min-height: calc(100vh - var(--tts-nav-height));
}
</style>
